
import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { getResponsiveImageUrl } from '@/lib/cloudinary';
import { useIsMobile } from '@/hooks/use-mobile';

interface AnimatedHeroTextProps {
  title: string;
  subtitle: string;
  className?: string;
}

interface HeroBackgroundImage {
  id: string;
  image_url: string;
  title: string | null;
}

export function AnimatedHeroText({ title, subtitle, className }: AnimatedHeroTextProps) {
  const words = title.split(' ');
  const [visibleWords, setVisibleWords] = useState<number[]>([]);
  const [showSubtitle, setShowSubtitle] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [highlightWorld, setHighlightWorld] = useState(false);
  const [backgroundImages, setBackgroundImages] = useState<HeroBackgroundImage[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState<{[key: string]: boolean}>({});
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isMobile = useIsMobile();
  
  // New ref to track mounted state
  const isMounted = useRef(true);
  
  // Keep track of preloaded images
  const [preloadedImages, setPreloadedImages] = useState<{[key: string]: HTMLImageElement}>({});

  // Fetch background images
  useEffect(() => {
    const fetchBackgroundImages = async () => {
      try {
        const { data, error } = await supabase
          .from('hero_background_images')
          .select('id, image_url, title')
          .eq('is_active', true)
          .order('display_order', { ascending: true });
        
        if (error) {
          console.error('Error fetching hero background images:', error);
          return;
        }
        
        if (data && data.length > 0 && isMounted.current) {
          setBackgroundImages(data);
          await preloadAllImages(data);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };
    
    fetchBackgroundImages();
    
    // Cleanup function
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Preload all images
  const preloadAllImages = async (images: HeroBackgroundImage[]) => {
    const preloadPromises = images.map(image => 
      preloadImage(getOptimizedImageUrl(image.image_url), image.id)
    );
    
    await Promise.all(preloadPromises);
  };

  // Preload a single image
  const preloadImage = (url: string, id: string): Promise<void> => {
    return new Promise((resolve) => {
      const img = new Image();
      
      img.onload = () => {
        if (isMounted.current) {
          setImagesLoaded(prev => ({ ...prev, [id]: true }));
          setPreloadedImages(prev => ({ ...prev, [id]: img }));
        }
        resolve();
      };
      
      img.onerror = () => {
        if (isMounted.current) {
          setImagesLoaded(prev => ({ ...prev, [id]: false }));
        }
        resolve();
      };
      
      img.src = url;
    });
  };

  // Handle image rotation with proper loading checks
  useEffect(() => {
    if (backgroundImages.length <= 1) return;
    
    let timer: NodeJS.Timeout;
    
    const rotateImages = () => {
      timer = setTimeout(() => {
        const nextIndex = (currentImageIndex + 1) % backgroundImages.length;
        
        // Only proceed if next image is loaded
        if (backgroundImages[nextIndex] && imagesLoaded[backgroundImages[nextIndex].id]) {
          setIsTransitioning(true);
          
          // Wait for fade transition to complete, then update the current image
          setTimeout(() => {
            if (isMounted.current) {
              setCurrentImageIndex(nextIndex);
              setIsTransitioning(false);
            }
          }, 1000); // Match with CSS transition duration
        }
        
        // Schedule next rotation
        if (isMounted.current) {
          rotateImages();
        }
      }, 8000); // Rotate every 8 seconds
    };
    
    // Start rotation when there are loaded images
    if (Object.values(imagesLoaded).some(loaded => loaded)) {
      rotateImages();
    }
    
    return () => {
      clearTimeout(timer);
    };
  }, [backgroundImages, currentImageIndex, imagesLoaded]);

  // Text animation effect
  useEffect(() => {
    // Reset animation state if title or subtitle changes
    setVisibleWords([]);
    setShowSubtitle(false);
    setShowButton(false);
    setHighlightWorld(false);
    
    // Animate words one by one
    const wordTimers: NodeJS.Timeout[] = [];
    
    words.forEach((_, index) => {
      const timer = setTimeout(() => {
        if (isMounted.current) {
          setVisibleWords(prev => [...prev, index]);
          
          // When last word is shown, highlight "World" after a short delay
          if (index === words.length - 1) {
            const highlightTimer = setTimeout(() => {
              if (isMounted.current) {
                setHighlightWorld(true);
                
                // Then show subtitle after another delay
                const subtitleTimer = setTimeout(() => {
                  if (isMounted.current) {
                    setShowSubtitle(true);
                    
                    // Show button after subtitle appears
                    const buttonTimer = setTimeout(() => {
                      if (isMounted.current) {
                        setShowButton(true);
                      }
                    }, 500);
                    
                    wordTimers.push(buttonTimer);
                  }
                }, 400);
                
                wordTimers.push(subtitleTimer);
              }
            }, 300);
            
            wordTimers.push(highlightTimer);
          }
        }
      }, 400 * (index + 1)); // 400ms delay between words
      
      wordTimers.push(timer);
    });
    
    return () => {
      wordTimers.forEach(timer => clearTimeout(timer));
    };
  }, [title, subtitle, words.length]);

  // Get optimized background image URL
  const getOptimizedImageUrl = (url: string, isLarge: boolean = true) => {
    if (url && url.includes('cloudinary.com')) {
      return getResponsiveImageUrl(url, isLarge ? 1920 : 1024);
    }
    return url || '';
  };

  return (
    <div className={cn("text-center relative overflow-hidden rounded-lg", className)}>
      {/* Background image container */}
      {backgroundImages.length > 0 && (
        <div className="absolute inset-0 w-full h-full">
          {/* Current image */}
          {backgroundImages[currentImageIndex] && (
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ 
                backgroundImage: `url(${getOptimizedImageUrl(backgroundImages[currentImageIndex].image_url)})`,
                zIndex: 1,
                transition: 'opacity 1s ease-in-out'
              }}
            />
          )}
          
          {/* Next image that fades in */}
          {backgroundImages.length > 1 && (
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ 
                backgroundImage: isTransitioning && backgroundImages[(currentImageIndex + 1) % backgroundImages.length] 
                  ? `url(${getOptimizedImageUrl(backgroundImages[(currentImageIndex + 1) % backgroundImages.length].image_url)})`
                  : 'none',
                opacity: isTransitioning ? 1 : 0,
                zIndex: isTransitioning ? 2 : 0,
                transition: 'opacity 1s ease-in-out'
              }}
            />
          )}
          
          {/* Dark overlay with texture for better text readability - more pronounced */}
          <div 
            className="absolute inset-0 z-10" 
            style={{
              background: `
                linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%),
                url("data:image/png;base64,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")
              `,
              pointerEvents: 'none',
              opacity: 0.95
            }}
          />
        </div>
      )}
      
      {/* Content */}
      <div className="relative z-20 px-4 sm:px-6 py-16 sm:py-20 md:py-28">
        <h1 className={cn(
          "font-bold mb-4 text-white",
          isMobile ? "text-3xl md:text-4xl" : "text-5xl"
        )}>
          {words.map((word, index) => (
            <span key={index}>
              <span 
                className={cn(
                  "inline-block transition-all duration-500",
                  visibleWords.includes(index) 
                    ? "opacity-100 translate-y-0" 
                    : "opacity-0 translate-y-4"
                )}
              >
                {/* Check if the current word is "World" - usually the last word in the title */}
                {word.toLowerCase() === "world" ? (
                  <span 
                    className={cn(
                      "transition-colors duration-700",
                      highlightWorld ? "text-blue-400" : ""
                    )}
                  >
                    {word}
                  </span>
                ) : (
                  word
                )}
              </span>
              {index < words.length - 1 && " "}
            </span>
          ))}
        </h1>
        
        <p 
          className={cn(
            "max-w-2xl mx-auto mb-6 transition-all duration-500 text-white",
            isMobile ? "text-base" : "text-xl",
            showSubtitle ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}
        >
          {subtitle}
        </p>
        
        {/* Button container - the actual button will be rendered by the parent */}
        <div 
          className={cn(
            "transition-all duration-500", 
            showButton ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}
        >
          {/* The parent will place the button here */}
        </div>
      </div>
    </div>
  );
}
